import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { InvoiceService } from 'api';
import { StyledButton } from 'components/Buttons';
import { StyledTextField } from 'components/Inputs';
import { StyledTableCell, StyledTableHeadCell } from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { useInvoicesContext } from 'contexts/invoicesContext';
import { debounce } from 'lodash';
import { ConfirmCancelDialog } from 'pages/invoices/components/InvoicesTable/components/InvoicesTableActions/components/ConfirmCancelDialog';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { IoIosResize } from 'react-icons/io';
import { IoSearch } from 'react-icons/io5';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { InvoiceTab, reactPaths, InvoiceType } from 'root/pathnames';
import {
  canAddInvoice,
  isDeleteEnabled,
  isInvoiceCorrective,
} from 'utils/invoiceFunctions.utils';
import * as S from './SideTable.css';

type Props = {
  onInvoiceChange: () => void;
  invoice: Invoice | CorrectiveInvoice;
};

export const SideTable = ({ onInvoiceChange, invoice }: Props) => {
  const { user, allowedReplacements } = useAuth();
  const { t } = useTranslation(['misc', 'invoice', 'datagrid']);

  const { tab: invoiceTab, invoiceId } = useParams<{
    tab: InvoiceTab;
    invoiceId: string;
  }>();

  const [loading, setLoading] = useState(true);

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const navigate = useNavigate();

  const { getInvoices, invoicesData } = InvoiceService.useGetInvoices();
  const { getTabParams, getSetTabParams } = useInvoicesContext();

  const params = getTabParams(invoiceTab!);
  const setParams = getSetTabParams(invoiceTab!);

  const [searchValue, setSearchValue] = useState(params.filterText);

  const debounceSearch = useCallback(
    debounce(
      (value: string) => setParams((prev) => ({ ...prev, filterText: value })),
      500,
    ),
    [],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    debounceSearch(e.target.value);
  };

  const handleRedirect = (i: Invoice) => () => {
    const isCorrective = i.invoiceClass.type
      .toLowerCase()
      .includes('corrective');
    onInvoiceChange();
    navigate(
      generatePath(reactPaths.invoiceDetails, {
        tab: invoiceTab,
        invoiceType: isCorrective
          ? InvoiceType.CORRECTIVE_INVOICE
          : InvoiceType.INVOICE,
        invoiceId: String(i.id),
      }),
    );
  };

  const handleChangePage = (_: unknown, newPage: number) =>
    setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await getInvoices(params);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getInvoices, params]);

  useEffect(() => {
    setParams((prev) => ({ ...prev, pageNumber: 1 }));
  }, [invoicesData?.count]); //TODO: In the future we can try limiting these useEffects into one (similarly to the SideTable for rates)

  return (
    <>
      <ConfirmCancelDialog
        open={isCancelDialogOpen}
        selectedInvoices={[
          {
            ...invoice,
            isCorrective: isInvoiceCorrective(invoice.invoiceClass),
          },
        ]}
        onClose={() => setIsCancelDialogOpen(false)}
        onSubmit={() =>
          navigate(
            generatePath(reactPaths.invoicesModule, {
              tab: invoiceTab,
            }),
          )
        }
      />
      <S.SideTableContainer>
        <S.ToolbarContainer>
          <S.ResizeButton
            size='small'
            onClick={() =>
              navigate(
                generatePath(reactPaths.invoicesModule, {
                  tab: invoiceTab,
                }),
              )
            }
          >
            <IoIosResize size='25' />
          </S.ResizeButton>
          <StyledTextField
            placeholder={t('button.search', { ns: 'misc' })}
            type='text'
            size='small'
            padding='dense'
            width={224}
            value={searchValue}
            onChange={handleSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IoSearch />
                </InputAdornment>
              ),
            }}
          />
        </S.ToolbarContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeadCell height='condensed'>
                {t('code', { ns: 'invoice' })}
              </StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from(new Array(10)).map((_, index) => (
                  <S.InvoicesTableRow key={index}>
                    <StyledTableCell height='condensed' padding='checkbox'>
                      <Skeleton variant='text' width={100} />
                    </StyledTableCell>
                  </S.InvoicesTableRow>
                ))
              : invoicesData?.invoices?.map((i) => (
                  <S.InvoicesTableRow
                    key={i.id}
                    selected={i.id === Number(invoiceId)}
                    $deleted={i.deleted}
                    $highlighted={i.containsComplainedAllocations}
                    hover
                    onClick={handleRedirect(i)}
                  >
                    <StyledTableCell height='condensed' padding='checkbox'>
                      {i.code}
                    </StyledTableCell>
                  </S.InvoicesTableRow>
                ))}
          </TableBody>
        </Table>
        {!!invoicesData?.count && (
          <TablePagination
            component='div'
            count={invoicesData.count}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
            page={params.pageNumber - 1}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from} - ${to} ${t('of', { ns: 'datagrid' })} ${count}`
            }
          />
        )}
        <S.ActionsContainer>
          <StyledButton
            $primary
            disabled={!invoiceTab || !canAddInvoice(invoiceTab, user)}
            onClick={() =>
              navigate(
                generatePath(reactPaths.invoicesForm, {
                  tab: invoiceTab,
                }),
              )
            }
          >
            {t('button.create', { ns: 'misc' })}
          </StyledButton>
          <StyledButton
            $primary
            disabled={
              !invoiceTab ||
              !isDeleteEnabled[invoiceTab]([invoice], user, allowedReplacements)
            }
            onClick={() => setIsCancelDialogOpen(true)}
          >
            {t('button.cancel', { ns: 'misc' })}
          </StyledButton>
        </S.ActionsContainer>
      </S.SideTableContainer>
    </>
  );
};
